import { usePagination } from "../../shared/lib/use-pagination.js";
import { Pagination } from "../../shared/ui/pagination.js";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useBots } from "../bots/queries.js";
import {
  useBacktestListSummary,
  useBacktestPage,
  useBacktestPnlChart,
  useCancelBacktest,
  useDeleteBacktest,
} from "../bots/backtest-queries.js";
import type { BacktestView } from "../bots/backtest-models.js";
import { ProblemNotice } from "../bots/problem-notice.js";
import { formatUsd } from "../positions/models.js";
import { Button } from "../../shared/ui/button.js";
import { RefreshButton } from "../../shared/ui/refresh-button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { backtestFilters, backtestPageSize, backtestStatuses } from "./backtest-list-models.js";
import { BacktestPnlChart } from "./backtest-pnl-chart.js";
import { DeleteFilteredBacktests } from "./delete-filtered-backtests.js";
import { backtestPnlGroup, backtestPnlGroups, pnlPeriodRange } from "./pnl-chart-models.js";

function dateTime(value: string | undefined): string {
  if (value === undefined || value === "") return "Unavailable";
  return new Date(value).toLocaleString();
}

function BacktestRunCard({
  run,
  bulkBusy,
}: {
  readonly run: BacktestView;
  readonly bulkBusy: boolean;
}) {
  const cancel = useCancelBacktest(run.id, run.botId);
  const deleteBacktest = useDeleteBacktest(run.botId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const active = run.status === "queued" || run.status === "running";
  const terminal = !active;

  return (
    <article className="backtest-index-card">
      <header>
        <div className="backtest-index-card__identity">
          <span className="symbol-mark">{run.request.symbol || "—"}</span>
          <div>
            <h2>{run.botName ?? `Bot ${run.botId}`}</h2>
            <p>
              {run.request.symbol || "Symbol unavailable"} · revision {run.revisionNumber || "—"}
            </p>
          </div>
        </div>
        <span className={`status-pill status-pill--${run.status}`}>{run.status}</span>
      </header>

      {active ? (
        <div className="progress-stack">
          <progress max="100" value={run.progressPercent}>
            {run.progressPercent}%
          </progress>
          <span>{run.progressPercent}% complete</span>
        </div>
      ) : null}

      <dl className="definition-grid backtest-index-card__details">
        <div>
          <dt>Test period</dt>
          <dd>
            {run.request.from || "—"} to {run.request.to || "—"}
          </dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{dateTime(run.createdAt)}</dd>
        </div>
        <div>
          <dt>Realized P/L</dt>
          <dd>{run.summary === undefined ? "—" : formatUsd(run.summary.realizedPnlUsd)}</dd>
        </div>
        <div>
          <dt>Fills</dt>
          <dd>{run.summary?.fillCount ?? "—"}</dd>
        </div>
      </dl>

      <div className="button-row">
        <Link className="button button--quiet" to={`/bots/${run.botId}/backtests/${run.id}`}>
          View details
        </Link>
        {run.status === "completed" && run.request.symbol !== "" ? (
          <Link
            className="button button--quiet"
            to={`/tickers/${encodeURIComponent(run.request.symbol)}?view=backtesting&backtestId=${encodeURIComponent(run.id)}`}
          >
            View chart
          </Link>
        ) : null}
        {active ? (
          <Button
            disabled={cancel.isPending}
            onClick={() => void cancel.mutateAsync()}
            tone="quiet"
          >
            {cancel.isPending ? "Cancelling…" : "Cancel run"}
          </Button>
        ) : null}
        {terminal && !confirmingDelete ? (
          <Button
            disabled={bulkBusy || deleteBacktest.isPending}
            onClick={() => {
              deleteBacktest.reset();
              setConfirmingDelete(true);
            }}
            tone="danger"
          >
            Delete result
          </Button>
        ) : null}
      </div>

      {confirmingDelete ? (
        <div
          aria-label={`Confirm deletion of backtest ${run.id}`}
          className="delete-confirmation"
          role="alertdialog"
        >
          <div>
            <strong>Delete this backtest result permanently?</strong>
            <p>The run record and its stored evidence cannot be recovered.</p>
          </div>
          <div className="button-row">
            <Button
              disabled={bulkBusy || deleteBacktest.isPending}
              onClick={() => setConfirmingDelete(false)}
              tone="quiet"
            >
              Keep result
            </Button>
            <Button
              disabled={bulkBusy || deleteBacktest.isPending}
              onClick={() =>
                deleteBacktest.mutate(run.id, {
                  onSuccess: () => setConfirmingDelete(false),
                })
              }
              tone="danger"
            >
              {deleteBacktest.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
          </div>
        </div>
      ) : null}
      {cancel.error === null ? null : <ProblemNotice error={cancel.error} />}
      {deleteBacktest.error === null ? null : <ProblemNotice error={deleteBacktest.error} />}
    </article>
  );
}

export function BacktestsPage() {
  const [search, setSearch] = useSearchParams();
  const [bulkBusy, setBulkBusy] = useState(false);
  const cursor = search.get("cursor") ?? "";
  const limit = backtestPageSize(search.get("limit") ?? "");
  const filters = backtestFilters(search);
  const view = search.get("view") === "chart" ? "chart" : "table";
  const groupBy = backtestPnlGroup(search.get("chartType"));
  const pnlRange = {
    ...(search.get("pnlFrom") === null ? {} : { pnlFrom: search.get("pnlFrom") ?? "" }),
    ...(search.get("pnlTo") === null ? {} : { pnlTo: search.get("pnlTo") ?? "" }),
  };
  const page = useBacktestPage(filters, cursor, limit, view === "table");
  const chart = useBacktestPnlChart(filters, groupBy, pnlRange, view === "chart");
  const summary = useBacktestListSummary(filters);
  const bots = useBots({});
  const pagination = usePagination(page.data?.items ?? []);
  const runs = pagination.items;

  const setFilter = (key: string, value: string) => {
    if (bulkBusy) return;
    const next = new URLSearchParams(search);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("cursor");
    next.delete("page");
    setSearch(next, { replace: true });
  };
  const clearFilters = () => {
    if (bulkBusy) return;
    const next = new URLSearchParams();
    if (limit !== 25) next.set("limit", String(limit));
    if (view === "chart") next.set("view", "chart");
    if (groupBy !== "date") next.set("chartType", groupBy);
    next.delete("page");
    setSearch(next, { replace: true });
  };
  const selectChartItem = (item: NonNullable<typeof chart.data>["items"][number]) => {
    if (groupBy === "symbol") {
      window.open(
        `/tickers/${encodeURIComponent(item.key)}?view=backtesting&backtestId=${encodeURIComponent(item.representativeBacktestId)}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    const range = pnlPeriodRange(groupBy, item.key);
    const next = new URLSearchParams(search);
    next.set("pnlFrom", range.pnlFrom);
    next.set("pnlTo", range.pnlTo);
    setSearch(next, { replace: true });
  };

  return (
    <section className="page-stack page-stack--compact" aria-labelledby="backtests-title">
      <header className="page-header page-header--action">
        <div>
          <p className="eyebrow">Research history</p>
          <h1 id="backtests-title">Backtests</h1>
          <p>Review durable results across Bots without starting another run.</p>
        </div>
        <div className="button-row">
          <RefreshButton queries={[view === "chart" ? chart : page, summary, bots]} />
          <Link className="button button--primary" to="/bots">
            Run backtests
          </Link>
        </div>
      </header>

      <div className="content-card backtest-summary" aria-label="Filtered backtest summary">
        <div>
          <span>Total runs</span>
          <strong>{summary.data?.totalRuns ?? "—"}</strong>
        </div>
        <div>
          <span>Active</span>
          <strong>
            {summary.data === undefined ? "—" : summary.data.queuedRuns + summary.data.runningRuns}
          </strong>
          {summary.data === undefined ? null : (
            <small>
              {summary.data.queuedRuns} queued · {summary.data.runningRuns} running
            </small>
          )}
        </div>
        <div>
          <span>Completed</span>
          <strong>{summary.data?.completedRuns ?? "—"}</strong>
          {summary.data === undefined ? null : (
            <small>
              {summary.data.failedRuns} failed · {summary.data.cancelledRuns} cancelled
            </small>
          )}
        </div>
        <div>
          <span>Realized P/L</span>
          <strong>
            {summary.data === undefined ? "—" : formatUsd(summary.data.realizedPnlUsd)}
          </strong>
          <small>Completed runs</small>
        </div>
        <div>
          <span>Fills</span>
          <strong>{summary.data?.fillCount ?? "—"}</strong>
          <small>Completed runs</small>
        </div>
      </div>
      {summary.error === null ? null : (
        <p className="backtest-summary__error">Filtered summary unavailable.</p>
      )}

      <div className="backtest-view-toolbar">
        <div className="tab-list" role="tablist" aria-label="Backtest result view">
          <button
            disabled={bulkBusy}
            aria-selected={view === "table"}
            onClick={() => setFilter("view", "table")}
            role="tab"
            type="button"
          >
            Table
          </button>
          <button
            disabled={bulkBusy}
            aria-selected={view === "chart"}
            onClick={() => setFilter("view", "chart")}
            role="tab"
            type="button"
          >
            Chart
          </button>
        </div>
      </div>

      <fieldset
        disabled={bulkBusy}
        className="content-card backtest-filters"
        aria-label="Backtest filters"
      >
        <div className="field-stack">
          <label htmlFor="backtest-index-bot">Bot</label>
          <select
            id="backtest-index-bot"
            value={search.get("bot") ?? ""}
            onChange={(event) => setFilter("bot", event.currentTarget.value)}
          >
            <option value="">All Bots</option>
            {(bots.data ?? []).map((bot) => (
              <option key={bot.id} value={bot.id}>
                {bot.name} · {bot.symbol}
              </option>
            ))}
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="backtest-index-symbol">Symbol</label>
          <input
            id="backtest-index-symbol"
            value={search.get("symbol") ?? ""}
            onChange={(event) => setFilter("symbol", event.currentTarget.value)}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="backtest-index-status">Status</label>
          <select
            id="backtest-index-status"
            value={search.get("status") ?? ""}
            onChange={(event) => setFilter("status", event.currentTarget.value)}
          >
            <option value="">All statuses</option>
            {backtestStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="backtest-index-from">Created from</label>
          <input
            id="backtest-index-from"
            type="date"
            value={search.get("from") ?? ""}
            onChange={(event) => setFilter("from", event.currentTarget.value)}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="backtest-index-to">Created to</label>
          <input
            id="backtest-index-to"
            type="date"
            value={search.get("to") ?? ""}
            onChange={(event) => setFilter("to", event.currentTarget.value)}
          />
        </div>
        {view === "table" ? (
          <div className="field-stack">
            <label htmlFor="backtest-index-limit">Page size</label>
            <select
              id="backtest-index-limit"
              value={limit}
              onChange={(event) => setFilter("limit", event.currentTarget.value)}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        ) : (
          <>
            <div className="field-stack">
              <label htmlFor="backtest-chart-type">Chart type</label>
              <select
                id="backtest-chart-type"
                value={groupBy}
                onChange={(event) => setFilter("chartType", event.currentTarget.value)}
              >
                {backtestPnlGroups.map((group) => (
                  <option key={group} value={group}>
                    P/L by {group[0]?.toUpperCase()}
                    {group.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-stack">
              <label htmlFor="backtest-pnl-from">Outcome from</label>
              <input
                id="backtest-pnl-from"
                type="date"
                value={search.get("pnlFrom") ?? ""}
                onChange={(event) => setFilter("pnlFrom", event.currentTarget.value)}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="backtest-pnl-to">Outcome to</label>
              <input
                id="backtest-pnl-to"
                type="date"
                value={search.get("pnlTo") ?? ""}
                onChange={(event) => setFilter("pnlTo", event.currentTarget.value)}
              />
            </div>
          </>
        )}
        <div className="backtest-filters__actions">
          <Button onClick={clearFilters} tone="quiet">
            Clear filters
          </Button>
        </div>
      </fieldset>

      {view === "table" ? (
        <DeleteFilteredBacktests filters={filters} onBusyChange={setBulkBusy} />
      ) : null}

      {view === "chart" ? (
        <div className="content-card section-stack backtest-chart-card">
          <div className="connection-heading">
            <div>
              <h2>Realized P/L</h2>
              <p className="section-description">
                Closing-trade outcomes use the America/New_York market session date.
              </p>
            </div>
            {chart.data === undefined ? null : (
              <span className="status-pill status-pill--completed">
                {chart.data.items.length} groups
              </span>
            )}
          </div>
          {chart.isLoading ? (
            <ResourceState
              kind="loading"
              title="Loading P/L chart"
              description="Aggregating completed backtest outcomes."
            />
          ) : chart.error !== null || chart.data === undefined ? (
            <ResourceState
              kind="error"
              title="P/L chart unavailable"
              description="TradingEngine could not aggregate the filtered outcomes."
            />
          ) : (
            <>
              {chart.data.coverage.missingRuns > 0 ? (
                <div className="truth-banner truth-banner--local-projection" role="status">
                  <strong>Historical chart coverage is incomplete</strong>
                  <span>
                    {chart.data.coverage.missingRuns} of {chart.data.coverage.completedRuns}{" "}
                    matching completed runs need the idempotent P/L projection backfill. Missing
                    runs are excluded, never counted as zero.
                  </span>
                </div>
              ) : null}
              {chart.data.items.length === 0 ? (
                <ResourceState
                  kind="empty"
                  title="No realized outcomes"
                  description="No projected closing trades match the current run and outcome filters."
                />
              ) : (
                <BacktestPnlChart
                  groupBy={groupBy}
                  items={chart.data.items}
                  onSelect={selectChartItem}
                />
              )}
            </>
          )}
        </div>
      ) : page.isLoading ? (
        <ResourceState
          kind="loading"
          title="Loading backtests"
          description="Reading durable research runs from TradingEngine."
        />
      ) : null}
      {view === "table" && page.error !== null ? (
        <ResourceState
          kind="error"
          title="Backtests unavailable"
          description="TradingEngine could not return the filtered research history."
        />
      ) : null}
      {bots.error !== null ? (
        <ResourceState
          kind="degraded"
          title="Bot filter labels unavailable"
          description="Backtest results remain available and retain their stored Bot names and IDs."
        />
      ) : null}
      {view === "table" && !page.isLoading && page.error === null && runs.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No matching backtests"
          description="Change the filters or launch research from the Bots page."
          action={
            <Link className="button button--primary" to="/bots">
              Open Bots
            </Link>
          }
        />
      ) : null}

      {view !== "table" || runs.length === 0 ? null : (
        <div className="backtest-index-grid">
          {runs.map((run) => (
            <BacktestRunCard key={run.id} run={run} bulkBusy={bulkBusy} />
          ))}
        </div>
      )}

      {view === "table" ? (
        <Pagination {...pagination} disabled={page.isFetching || page.error !== null} />
      ) : null}
    </section>
  );
}
